import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { addMonths, format, startOfMonth, subMonths } from 'date-fns';
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Loader2,
  MapPin,
  X,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { PhoneInput } from '@/components/molecules/PhoneInput';
import { GoogleAddressAutocomplete } from '@/components/molecules/GoogleAddressAutocomplete';
import { useBookingLinkConfig } from '@/hooks/onlineBooking/useBookingLinkConfig';
import { CoverageBanner } from '@/components/scheduler/CoverageBanner';
import { SurveyForm } from '@/components/onlineBooking/SurveyForm';
import { OrderSummary } from '@/components/onlineBooking/OrderSummary';
import { brandStyleVars } from '@/components/onlineBooking/brandTheme';
import {
  bookingSubmissionErrorMessage,
  isRetryableBookingSlotConflict,
} from '@/components/onlineBooking/bookingSubmissionError';
import {
  bookingAttemptForSelection,
  type BookingAttempt,
} from '@/components/onlineBooking/bookingAttempt';
import { useAvailability } from '@/hooks/onlineBooking/useAvailability';
import { useBookingQuote } from '@/hooks/onlineBooking/useBookingQuote';
import { useCreateOnlineBooking } from '@/hooks/onlineBooking/useCreateOnlineBooking';
import { isEmbedded, postToHost } from '@/lib/embed/bookingEmbedBridge';
import {
  dateStringToDate,
  formatDateTimeInTimeZone,
  formatWhenInTimeZone,
  formatTimeZoneLabel,
} from '@/lib/date-values';
import type {
  BookingStep,
  DayAvailability,
  OnlineBookingConfirmation,
  PublicService,
  SurveyAnswers,
  TimeSlot,
} from '@/types/onlineBooking';

export const Route = createFileRoute('/(public)/onlinebooking/$id')({
  component: OnlineBookingWizard,
  // ?embed=1 — the wizard is inside the embed loader's modal iframe on a
  // customer site: tighter chrome, a close button, postMessage lifecycle.
  validateSearch: (search: Record<string, unknown>) => ({
    embed: search.embed === '1' || search.embed === 1 || search.embed === true,
  }),
});

/**
 * The app shell locks `html, body, #root` to `overflow: hidden` (the dashboard
 * manages its own inner scroll regions). This public page uses normal page
 * flow, so it needs to own a viewport-height scroll container of its own —
 * otherwise long content (e.g. the month of time slots) is clipped with no way
 * to scroll to it. Wraps every render branch (loading / error / wizard /
 * confirmation) below.
 */
function OnlineBookingWizard() {
  const { id: linkKey } = Route.useParams();
  const { data: config } = useBookingLinkConfig(linkKey);
  // Brand the whole wizard by re-pointing the theme's --primary tokens at the
  // link's themeColor (falling back to the tenant's primaryColor). Applied on
  // the scroll container so every branch (loading/wizard/confirmation) themes.
  const themeColor =
    (config?.config?.themeColor as string | undefined) ?? config?.branding.primaryColor;
  return (
    <div className="h-dvh overflow-y-auto" style={brandStyleVars(themeColor)}>
      <OnlineBookingWizardInner />
    </div>
  );
}

// Pinned to en-US like the rest of this public page: the visitor's browser
// locale would otherwise render a USD amount as "$1.234,5".
const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

interface AddressState {
  street: string;
  city: string;
  state: string;
  zip: string;
}
interface ContactState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

const emptyAddress: AddressState = { street: '', city: '', state: '', zip: '' };
const emptyContact: ContactState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  notes: '',
};

function OnlineBookingWizardInner() {
  const { id: linkKey } = Route.useParams();
  const { embed } = Route.useSearch();
  const embedded = isEmbedded(embed);
  const { data: config, isLoading, isError } = useBookingLinkConfig(linkKey);

  const [step, setStep] = useState<BookingStep>('service');
  const [service, setService] = useState<PublicService | null>(null);
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswers | null>(null);
  const [address, setAddress] = useState<AddressState>(emptyAddress);
  const [selectedOptionalIds, setSelectedOptionalIds] = useState<string[]>([]);
  // Set when the chosen time was taken before we could book it — drives the
  // explanatory banner on the time picker.
  const [slotTaken, setSlotTaken] = useState(false);
  // The street value whose city/state/zip we last derived from an autocomplete
  // pick. When the street is edited away from it, the derived fields are stale
  // and must be cleared so the customer re-picks (and availability, which keys
  // on zip, isn't run against a mismatched location).
  const derivedFromStreet = useRef<string | null>(null);
  const [month, setMonth] = useState<Date>(startOfMonth(new Date()));
  const [slot, setSlot] = useState<TimeSlot | null>(null);
  const [contact, setContact] = useState<ContactState>(emptyContact);
  const [confirmation, setConfirmation] = useState<OnlineBookingConfirmation | null>(null);
  // React Query only flips isPending once mutateAsync starts. submit does an
  // availability refetch first, leaving a lag-sized window where another click
  // could start a second candidate loop. This synchronous lock closes that gap.
  const submissionInFlight = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Every retry for one customer-visible service/time selection shares a server
  // idempotency key, even if an availability refresh changes the hidden slot ID.
  const bookingAttempt = useRef<BookingAttempt | null>(null);

  const createBooking = useCreateOnlineBooking(linkKey);
  const monthKey = format(month, 'yyyy-MM');
  const availabilityQuery = useAvailability(linkKey, service?.id ?? '', monthKey, address.zip);
  // Hidden-price links never fetch amounts at all (nothing on the wire to peek
  // at) — the summary renders 'Quoted' without a quote.
  const showPrices = config?.config?.showPrices !== 'false';
  // Warms as soon as the zip is known so the confirm-step order summary (lines
  // + tax for this address) renders instantly. Never blocks booking.
  const quoteQuery = useBookingQuote({
    linkKey,
    serviceId: service?.id,
    hasPricebookItems: service?.hasPricebookItems ?? false,
    showPrices,
    address,
    selectedOptionalLineItemIds: selectedOptionalIds,
  });

  // Embed lifecycle: tell the host page the wizard mounted, and when a booking
  // completes (analytics hook on the customer site).
  useEffect(() => {
    if (embedded) postToHost({ source: 'notifi-booking', type: 'ready' });
  }, [embedded]);
  useEffect(() => {
    if (embedded && confirmation) {
      postToHost({
        source: 'notifi-booking',
        type: 'booking-completed',
        referenceNumber: confirmation.referenceNumber,
        serviceName: confirmation.serviceName,
      });
    }
  }, [embedded, confirmation]);

  if (isLoading) {
    return <Centered>Loading…</Centered>;
  }
  if (isError || !config) {
    return <Centered>This booking link is not available.</Centered>;
  }

  // The business tz the slots are anchored to — render + label in it, never the
  // customer's browser tz, so the time they pick equals the confirmation.
  const bookingTz = availabilityQuery.data?.timezone ?? config.branding.timezone ?? null;

  /** Send the customer back to a FRESH time picker after their slot went away. */
  const bounceToTimePicker = () => {
    setSlot(null);
    bookingAttempt.current = null;
    setSlotTaken(true);
    setStep('datetime');
    // Clear the failed attempt too: otherwise the customer picks a fresh time,
    // returns to Confirm, and is greeted by the previous failure's error banner
    // before they have tried anything.
    createBooking.reset();
    void availabilityQuery.refetch();
  };

  const submit = async () => {
    if (!service || !slot || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setIsSubmitting(true);
    const attempt = bookingAttemptForSelection(
      bookingAttempt.current,
      `${service.id}:${slot.start}`,
    );
    bookingAttempt.current = attempt;
    let completed = false;
    try {
      const refreshed = await availabilityQuery.refetch();
      // A slot can be claimed by someone else while the customer fills in their
      // details. When the refetch SUCCEEDS and no longer offers the chosen time,
      // the booking cannot succeed — sending it anyway just burns a round-trip to
      // guarantee a 409 and leaves them stuck on this screen. Bounce them to the
      // picker with current times instead. (If the refetch itself failed we have
      // no fresh truth, so fall through and let the server decide.)
      const fresh = refreshed.data?.availability;
      if (fresh && slotsMatchingStart(fresh, slot).length === 0) {
        bounceToTimePicker();
        return;
      }
      const candidateSlots = orderBySelectedSlot(
        slotsMatchingStart(fresh, slot).length > 0
          ? slotsMatchingStart(fresh, slot)
          : slotsMatchingStart(availabilityQuery.data?.availability, slot),
        slot,
      );
      const slotsToTry = candidateSlots.length > 0 ? candidateSlots : [slot];
      for (const candidate of slotsToTry) {
        try {
          const data = await createBooking.mutateAsync({
            idempotencyKey: attempt.idempotencyKey,
            serviceId: service.id,
            slotId: candidate.slotId,
            technicianId: candidate.technicianId,
            scheduledStart: candidate.start,
            scheduledEnd: candidate.end,
            address: address.street,
            zip: address.zip,
            city: address.city || undefined,
            state: address.state || undefined,
            contact: {
              firstName: contact.firstName.trim(),
              lastName: contact.lastName.trim(),
              email: contact.email.trim(),
              phone: contact.phone.trim(),
              notes: contact.notes.trim() || undefined,
            },
            ...(service.survey && surveyAnswers ? { formAnswers: surveyAnswers } : {}),
            ...(selectedOptionalIds.length > 0
              ? { selectedOptionalLineItemIds: selectedOptionalIds }
              : {}),
          });
          completed = true;
          setSlot(candidate);
          setConfirmation(data);
          return;
        } catch (error) {
          if (!isRetryableBookingSlotConflict(error)) return;
          // This technician's slot was explicitly taken. Try the next hidden
          // candidate for the same customer-visible time.
        }
      }
      // Every technician offering that time lost the race. Same recovery: don't
      // strand them on a confirm screen whose slot is dead.
      bounceToTimePicker();
    } finally {
      // Keep a successful confirmation permanently locked while React swaps in
      // the success view; failed attempts may be retried after picking a slot.
      if (!completed) {
        submissionInFlight.current = false;
        setIsSubmitting(false);
      }
    }
  };

  // Tenant-authored copy knobs (rendered as text only — never markup).
  const appearance = config.config ?? {};
  const headline = (appearance.headline as string | undefined) || 'Book a service online';
  const welcomeMessage = (appearance.welcomeMessage as string | undefined) || null;

  if (confirmation) {
    return (
      <ConfirmationView
        confirmation={confirmation}
        company={config.branding.name}
        timezone={confirmation.timezone ?? bookingTz}
        embedded={embedded}
      />
    );
  }

  return (
    <div className={`mx-auto max-w-xl px-4 ${embedded ? 'py-4' : 'py-8'}`}>
      <header className={`${embedded ? 'mb-4' : 'mb-6'} flex items-center gap-3`}>
        {config.branding.logoUrl && (
          <img src={config.branding.logoUrl} alt="" className="h-9 w-9 rounded object-contain" />
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold">{config.branding.name}</h1>
          <p className="text-xs text-muted-foreground">{headline}</p>
        </div>
        {embedded && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close booking"
            onClick={() => postToHost({ source: 'notifi-booking', type: 'close' })}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </header>
      {welcomeMessage && step === 'service' && (
        <p className="mb-4 text-sm text-muted-foreground">{welcomeMessage}</p>
      )}

      <Steps current={step} showSurvey={Boolean(service?.survey)} />

      {step === 'service' && (
        <section className="space-y-3">
          {config.services.length === 0 ? (
            <Empty>No services are available to book right now.</Empty>
          ) : (
            config.services.map((svc) => (
              <button
                key={svc.id}
                onClick={() => {
                  setService(svc);
                  setSurveyAnswers(null);
                  setSelectedOptionalIds([]);
                  setStep(svc.survey ? 'survey' : 'address');
                }}
                className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition hover:border-primary hover:bg-muted/40"
              >
                {svc.imageUrl && (
                  <img
                    src={svc.imageUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{svc.name}</div>
                  {svc.description && (
                    <div className="line-clamp-2 text-sm text-muted-foreground">
                      {svc.description}
                    </div>
                  )}
                  <div className="mt-1 text-sm text-muted-foreground">
                    {!showPrices
                      ? 'Quoted'
                      : svc.priceCents > 0
                        ? // Pricebook-quoted services get a live itemized total
                          // later (address-dependent tax), so the card price is
                          // a starting point, not the checkout number.
                          `${svc.hasPricebookItems ? 'From ' : ''}${money(svc.priceCents)}`
                        : 'Quoted'}{' '}
                    · {svc.durationMinutes} min
                  </div>
                </div>
              </button>
            ))
          )}
        </section>
      )}

      {step === 'survey' && service?.survey && (
        <StepCard
          title={service.survey.name || 'A few questions'}
          icon={<ClipboardList className="h-4 w-4" />}
          onBack={() => setStep('service')}
        >
          <SurveyForm
            survey={service.survey}
            initialAnswers={surveyAnswers}
            timeZone={bookingTz}
            onSubmit={(answers) => {
              setSurveyAnswers(answers);
              setStep('address');
            }}
          />
        </StepCard>
      )}

      {step === 'address' && service && (
        <StepCard
          title="Where's the service?"
          icon={<MapPin className="h-4 w-4" />}
          onBack={() => setStep(service.survey ? 'survey' : 'service')}
        >
          <GoogleAddressAutocomplete
            label="Street address"
            placeholder="Start typing an address…"
            value={address.street}
            onChange={(street) =>
              setAddress((a) => {
                // A pick echoes the same street back through onChange — keep the
                // just-derived city/state/zip in that case. Any other edit is a
                // manual change, so drop the now-stale derived fields.
                if (street === derivedFromStreet.current) return { ...a, street };
                derivedFromStreet.current = null;
                return { ...a, street, city: '', state: '', zip: '' };
              })
            }
            onAddressSelect={(parsed) => {
              // Overwrite city/state/zip from the newly-picked place (no stale
              // fallback) so re-picking a different address recalculates them.
              const street = parsed.street || address.street;
              derivedFromStreet.current = street;
              setAddress({
                street,
                city: parsed.city,
                state: parsed.state,
                zip: parsed.zip,
              });
            }}
          />
          <div className="grid grid-cols-3 gap-2">
            <Field label="City">
              <Input
                value={address.city}
                onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))}
              />
            </Field>
            <Field label="State">
              <Input
                value={address.state}
                onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
              />
            </Field>
            <Field label="ZIP">
              <Input
                value={address.zip}
                onChange={(e) => setAddress((a) => ({ ...a, zip: e.target.value }))}
              />
            </Field>
          </div>
          <Button
            className="w-full"
            disabled={!address.street.trim() || !address.zip.trim()}
            onClick={() => setStep('datetime')}
          >
            See available times
          </Button>
        </StepCard>
      )}

      {step === 'datetime' && service && (
        <StepCard
          title="Pick a time"
          icon={<Calendar className="h-4 w-4" />}
          onBack={() => setStep('address')}
        >
          {slotTaken && (
            <p
              role="status"
              className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              Sorry — someone booked that time while you were filling in your details. Here are the
              times still available.
            </p>
          )}
          <div className="mb-3 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              disabled={format(month, 'yyyy-MM') <= format(new Date(), 'yyyy-MM')}
              onClick={() => setMonth((m) => subMonths(m, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium">{format(month, 'MMMM yyyy')}</span>
            <Button variant="ghost" size="icon" onClick={() => setMonth((m) => addMonths(m, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {availabilityQuery.isLoading ? (
            <Centered>Finding times…</Centered>
          ) : (availabilityQuery.data?.availability.length ?? 0) === 0 ? (
            // Only surface the coverage warning when there's nothing to book —
            // it explains *why* the month is empty. When slots ARE returned the
            // customer can book, so the banner (whose skill-based coverage check
            // can disagree with the availability engine) must not contradict a
            // list of bookable times above.
            <>
              <CoverageBanner
                zipCode={address.zip || null}
                serviceTypeId={service.serviceTypeId ?? null}
                variant="public"
                tenantId={config.tenantId}
                className="mb-4"
              />
              <Empty>No available times this month. Try another month.</Empty>
            </>
          ) : (
            <div className="space-y-4">
              {showPrices && service.hasPricebookItems && quoteQuery.data?.mode === 'LINES' && (
                <p className="text-xs text-muted-foreground">
                  Estimated total for your address:{' '}
                  <span className="font-medium text-foreground">
                    ${quoteQuery.data.total.toFixed(2)}
                  </span>{' '}
                  incl. tax
                </p>
              )}
              {bookingTz ? (
                <p className="text-xs text-muted-foreground">
                  Times shown in {formatTimeZoneLabel(bookingTz)}
                </p>
              ) : null}
              {availabilityQuery.data!.availability.map((day) => (
                <div key={day.date}>
                  <div className="mb-1.5 text-sm font-medium">
                    {format(dateStringToDate(day.date)!, 'EEE, MMM d')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {dedupeByStart(day.slots).map((s) => (
                      <button
                        key={s.slotId}
                        onClick={() => {
                          setSlot(s);
                          setSlotTaken(false);
                          createBooking.reset();
                          setStep('contact');
                        }}
                        className="rounded-md border px-3 py-1.5 text-sm transition hover:border-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        {formatDateTimeInTimeZone(s.start, bookingTz, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </StepCard>
      )}

      {step === 'contact' && service && slot && (
        <StepCard
          title="Your details"
          icon={<Clock className="h-4 w-4" />}
          onBack={() => setStep('datetime')}
        >
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name">
              <Input
                value={contact.firstName}
                onChange={(e) => setContact((c) => ({ ...c, firstName: e.target.value }))}
              />
            </Field>
            <Field label="Last name">
              <Input
                value={contact.lastName}
                onChange={(e) => setContact((c) => ({ ...c, lastName: e.target.value }))}
              />
            </Field>
          </div>
          <Field label="Email">
            <Input
              type="email"
              value={contact.email}
              onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
            />
          </Field>
          <Field label="Phone">
            <PhoneInput
              value={contact.phone}
              onChange={(phone) => setContact((c) => ({ ...c, phone }))}
            />
          </Field>
          <Field label="Notes (optional)">
            <Textarea
              rows={2}
              value={contact.notes}
              onChange={(e) => setContact((c) => ({ ...c, notes: e.target.value }))}
            />
          </Field>
          <Button
            className="w-full"
            onClick={() => setStep('confirm')}
            disabled={!contactValid(contact)}
          >
            Review booking
          </Button>
        </StepCard>
      )}

      {step === 'confirm' && service && slot && (
        <StepCard
          title="Confirm"
          icon={<CheckCircle2 className="h-4 w-4" />}
          onBack={() => setStep('contact')}
        >
          <dl className="space-y-2 text-sm">
            <Row label="Service" value={service.name} />
            <Row
              label="When"
              value={formatWhenInTimeZone(slot.start, bookingTz, {
                weekday: 'short',
                month: 'short',
              })}
            />
            <Row
              label="Where"
              value={`${address.street}, ${address.city} ${address.state} ${address.zip}`.trim()}
            />
            <Row
              label="Contact"
              value={`${contact.firstName} ${contact.lastName} · ${contact.email}`}
            />
          </dl>
          <OrderSummary
            quote={quoteQuery.data}
            isLoading={quoteQuery.isLoading}
            isError={quoteQuery.isError}
            service={service}
            showPrices={showPrices}
            onToggleOptional={(lineItemId, selected) =>
              setSelectedOptionalIds((prev) =>
                selected ? [...prev, lineItemId] : prev.filter((id) => id !== lineItemId),
              )
            }
          />
          {createBooking.isError && (
            <p className="text-sm text-destructive">
              {bookingSubmissionErrorMessage(createBooking.error)}
            </p>
          )}
          <Button className="w-full" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm booking'}
          </Button>
        </StepCard>
      )}
    </div>
  );
}

function contactValid(c: ContactState) {
  return (
    c.firstName.trim().length > 0 &&
    c.lastName.trim().length > 0 &&
    /.+@.+\..+/.test(c.email) &&
    c.phone.trim().length >= 7
  );
}

const STEP_ORDER: BookingStep[] = ['service', 'address', 'datetime', 'contact', 'confirm'];
const STEP_ORDER_WITH_SURVEY: BookingStep[] = [
  'service',
  'survey',
  'address',
  'datetime',
  'contact',
  'confirm',
];

function Steps({ current, showSurvey }: { current: BookingStep; showSurvey: boolean }) {
  const order = showSurvey ? STEP_ORDER_WITH_SURVEY : STEP_ORDER;
  const idx = order.indexOf(current);
  return (
    <div className="mb-6 flex gap-1.5">
      {order.map((s, i) => (
        <div
          key={s}
          className={`h-1.5 flex-1 rounded-full ${i <= idx ? 'bg-primary' : 'bg-muted'}`}
        />
      ))}
    </div>
  );
}

function StepCard({
  title,
  icon,
  onBack,
  children,
}: {
  title: string;
  icon: ReactNode;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="flex items-center gap-1.5 font-medium">
          {icon}
          {title}
        </span>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

/**
 * One chip per TIME — several technicians can offer the same start instant and
 * the customer never picks a technician, so duplicated times only confuse.
 * Keep the first slot per start (the server re-validates the exact slot on
 * confirm and assigns the technician).
 */
function dedupeByStart<T extends { start: string }>(slots: T[]): T[] {
  const seen = new Set<string>();
  return slots.filter((s) => {
    if (seen.has(s.start)) return false;
    seen.add(s.start);
    return true;
  });
}

function slotsMatchingStart(availability: DayAvailability[] | undefined, selected: TimeSlot) {
  return (
    availability
      ?.flatMap((day) => day.slots)
      .filter((candidate) => candidate.start === selected.start) ?? []
  );
}

function orderBySelectedSlot(slots: TimeSlot[], selected: TimeSlot) {
  return [...slots].sort((a, b) => {
    if (a.slotId === selected.slotId) return -1;
    if (b.slotId === selected.slotId) return 1;
    return 0;
  });
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function ConfirmationView({
  confirmation,
  company,
  timezone,
  embedded = false,
}: {
  confirmation: OnlineBookingConfirmation;
  company: string;
  timezone: string | null;
  embedded?: boolean;
}) {
  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center">
      <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-primary" />
      <h1 className="text-xl font-semibold">You're booked!</h1>
      <p className="mt-2 text-muted-foreground">
        {company} will see you on{' '}
        <span className="font-medium text-foreground">
          {formatDateTimeInTimeZone(confirmation.scheduledStart, timezone, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
          {/* §11.3 — show the promised arrival window when one applies; otherwise
              the precise start time. */}
          {confirmation.arrivalWindow ? (
            <>
              {' '}
              between{' '}
              {formatDateTimeInTimeZone(confirmation.arrivalWindow.start, timezone, {
                hour: 'numeric',
                minute: '2-digit',
              })}
              {'–'}
              {formatDateTimeInTimeZone(confirmation.arrivalWindow.end, timezone, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </>
          ) : (
            <>
              {' '}
              at{' '}
              {formatDateTimeInTimeZone(confirmation.scheduledStart, timezone, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </>
          )}
        </span>
        {timezone ? ` ${formatTimeZoneLabel(timezone)}` : ''}.
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        Confirmation #{confirmation.referenceNumber} · sent to {confirmation.contactEmail}
      </p>
      {confirmation.depositRequired &&
        (confirmation.depositUrl ? (
          <div className="mx-auto mt-8 max-w-sm rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              A{' '}
              <span className="font-medium text-foreground">
                ${confirmation.depositAmount?.toFixed(2)}
              </span>{' '}
              deposit secures your appointment.
            </p>
            <Button
              className="mt-3 w-full"
              onClick={() => {
                // Embedded: the payment page must not be trapped inside the
                // host site's modal iframe — open it as a first-class tab.
                if (embedded) {
                  window.open(confirmation.depositUrl!, '_blank', 'noopener');
                } else {
                  window.location.href = confirmation.depositUrl!;
                }
              }}
            >
              Pay deposit
            </Button>
          </div>
        ) : (
          // The deposit link couldn't be created (backend degrades rather than
          // failing the booking) — the appointment is confirmed; the company
          // collects the deposit separately.
          <div className="mx-auto mt-8 max-w-sm rounded-lg border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              A{' '}
              <span className="font-medium text-foreground">
                ${confirmation.depositAmount?.toFixed(2)}
              </span>{' '}
              deposit is required for this service. Online payment isn't available right now —{' '}
              {company} will contact you to collect the deposit.
            </p>
          </div>
        ))}
    </div>
  );
}
